import React, { ChangeEvent } from 'react';
import { Progress } from '@umu/design-base';
import { EMediaType } from '@umu/umu-types';
import UploadQueue from '../../uploaderQueue';
import { IUploadQueueOptions, EUploadStatus, IPayloadData } from '../../uploaderQueue';
import { UploadFile } from '../../../../components/uploadImageAndCrop/uploadImageAndCropDialog';
import { Iconfont, Tooltip } from '@umu/umu-design';
import { DEFAULT_PROPS } from './constants';
import { getFileSize } from './utils';
import './index.scss';

interface IUploadDialog {
    mediaType: EMediaType;
    teacherId: string;
    accept?: string;
    multiple?: boolean;
    children: string | JSX.Element;
}

interface IUploadDialogOptions extends IUploadDialog {
    container?: HTMLElement | null;
    onClose?: () => void;
    // 最大并发数
    maxUploadConcurrent?: number;
    onSuccess?: (file: IUploadQueueOptions, data: IPayloadData) => void;
    onError?: (file: IUploadQueueOptions, error: any) => void;
    onProgress?: (file: IUploadQueueOptions, progress: number) => void;
    onBeforeUpload?: (file: File) => boolean;
    getIconElementByFileType?: (fileType: string) => React.ReactNode;
    onAllComplete?: (data: IPayloadData[]) => void;
}

interface IUploadDialogState {
    fileList: IUploadQueueOptions[];
    showList: boolean;
    showModal: boolean;
}

class UploaderDialog extends React.Component<IUploadDialogOptions, IUploadDialogState> {
    private uploadQueue: UploadQueue;

    constructor(props: IUploadDialogOptions) {
        super(props);
        const { maxUploadConcurrent = DEFAULT_PROPS.maxUploadConcurrent, onAllComplete = () => {} } = props;
        this.state = {
            fileList: [],
            showList: true,
            showModal: false,
        };
        this.onChooseFile = this.onChooseFile.bind(this);
        this.handleRetry = this.handleRetry.bind(this);
        this.onDropdown = this.onDropdown.bind(this);
        this.onClose = this.onClose.bind(this);

        this.uploadQueue = new UploadQueue(maxUploadConcurrent, {
            onAllComplete: (payloadDataList: IPayloadData[]) => {
                if (onAllComplete) {
                    onAllComplete(payloadDataList);
                }
            },
        });
    }

    addUploadQueue(newFile: IUploadQueueOptions) {
        this.uploadQueue
            .add(newFile)
            .then((value: IPayloadData) => {
                this.setState((prevState) => ({
                    fileList: prevState.fileList.map((item) =>
                        item.fileName === newFile.fileName ? { ...item, status: EUploadStatus.success } : item,
                    ),
                }));
                if (this.props.onSuccess) {
                    this.props.onSuccess(newFile, value);
                }
            })
            .catch((error) => {
                console.error('upload fail: ', error);
                if (this.props.onError) {
                    this.props.onError(newFile, error);
                }
                this.setState((prevState) => ({
                    fileList: prevState.fileList.map((item) =>
                        item.fileName === newFile.fileName ? { ...item, status: EUploadStatus.error } : item,
                    ),
                }));
            });
    }

    onDropdown() {
        this.setState((prevState) => ({
            showList: !prevState.showList,
        }));
    }

    onClose() {
        const { onClose } = this.props;
        if (onClose) {
            onClose();
        }
        this.setState({
            showModal: false,
        });
    }

    public handleUpload(file: File) {
        const { mediaType, teacherId, onProgress, onBeforeUpload = DEFAULT_PROPS.onBeforeUpload } = this.props;
        if (!file) {
            return;
        }
        if (onBeforeUpload && !onBeforeUpload(file, this.props.accept)) {
            console.error(`File type ${file.type} is not accepted.`);
            return;
        }
        const newFile: IUploadQueueOptions = {
            blob: file,
            ext: file.name.split('.').pop() || '',
            previewUrl: '',
            fileName: file.name,
            mediaType: mediaType,
            teacherId: teacherId,
            progress: { percent: 0 },
            status: EUploadStatus.uploading,
            onProgress: (fileName, progress) => {
                this.setState((prevState) => ({
                    fileList: prevState.fileList.map((item) =>
                        item.fileName === fileName ? { ...item, progress } : item,
                    ),
                }));
                if (onProgress) {
                    onProgress(newFile, progress.percent);
                }
            },
        };

        this.setState((prevState) => ({
            fileList: [...prevState.fileList, newFile],
        }));

        this.addUploadQueue(newFile);
    }

    public onChooseFile(ev: ChangeEvent<HTMLInputElement>) {
        const file = ev.target.files;
        if (!file) {
            return;
        }
        const files = Array.from(file);
        for (const item of files) {
            this.handleUpload(item);
            this.setState({
                showModal: true,
            });
        }
    }

    public handleRetry(file: IUploadQueueOptions) {
        this.setState((prevState) => ({
            fileList: prevState.fileList.map((item) =>
                item.fileName === file.fileName ? { ...item, status: EUploadStatus.uploading } : item,
            ),
        }));
        this.addUploadQueue(file);
    }

    public renderChooseFile() {
        const { accept, multiple, children = '' } = this.props;
        return (
            <UploadFile accept={accept} multiple={multiple} onChooseFile={this.onChooseFile}>
                {children}
            </UploadFile>
        );
    }

    public renderUploadListHeader() {
        const { showList } = this.state;
        return (
            <div className="header">
                <div className="action">
                    <Iconfont
                        style={{
                            transform: !showList ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                        }}
                        type="DropDown"
                        onClick={this.onDropdown}
                    ></Iconfont>
                    <Iconfont type="Close" onClick={this.onClose}></Iconfont>
                </div>
            </div>
        );
    }

    public getIconByFileType(fileType: string) {
        const { getIconElementByFileType = DEFAULT_PROPS.getIconElementByFileType } = this.props;

        return getIconElementByFileType(fileType);
    }

    public renderUploadListItem(item: IUploadQueueOptions) {
        return (
            <div key={item.fileName} className="item">
                <div className="file-icon">{this.getIconByFileType(item.ext)}</div>
                <Tooltip title={item.fileName}>
                    <div className="upload-file-name">{item.fileName}</div>
                </Tooltip>
                <div className="file-size">{getFileSize(item.blob.size)}</div>
                <div className="status">
                    {item.status === EUploadStatus.uploading && (
                        <Progress type="circle" percent={item.progress.percent} size={20} />
                    )}
                    {item.status === EUploadStatus.success && (
                        <Iconfont className="upload-icon-success" type="Completed"></Iconfont>
                    )}
                    {item.status === EUploadStatus.error && (
                        <div className="retry">
                            <Iconfont className="upload-fail-icon" type="ExclamationCircleFilled"></Iconfont>
                            <Iconfont
                                className="upload-reupload-icon"
                                type="ReUpload"
                                onClick={() => this.handleRetry(item)}
                            ></Iconfont>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    public renderUploadList() {
        const { fileList, showList } = this.state;
        if (!showList) {
            return null;
        }
        return (
            <div className="list-container">
                <div className="list">
                    {fileList.map((item) => {
                        return this.renderUploadListItem(item);
                    })}
                </div>
            </div>
        );
    }

    public renderUploadModal() {
        const { fileList, showModal } = this.state;
        if (fileList.length === 0 || !showModal) {
            return null;
        }
        return (
            <div className="uploader-dialog-file-list-container">
                {this.renderUploadListHeader()}
                {this.renderUploadList()}
            </div>
        );
    }

    render() {
        return (
            <>
                {this.renderChooseFile()}
                {this.renderUploadModal()}
            </>
        );
    }
}

export default UploaderDialog;

export type { IUploadDialog, IUploadDialogOptions, IUploadDialogState, IUploadQueueOptions, EUploadStatus };
